import { startAuthFlow, startPdeAuthFlow, exchangeCodeForTokens, logout as logoutFromAecomai, getSession as getSessionFromAecomai, setSession as setSessionFromAecomai, clearSession, refreshTokens, getValidSession, } from '@aecom-ai-engineering/auth';
import { isPdeEnvironment } from './config';
import { AUTH_STATE_CHANGE_EVENT } from './react-context';
// Storage keys for saved path
const PREV_PATH_KEY = 'auth.prevPath';
/** Store the path before authentication with Azure */
export const savePathBeforeAuth = (path) => {
    sessionStorage.setItem(PREV_PATH_KEY, path);
};
/**
 * Take the path that was saved before authentication with Azure. The key is
 * removed on read, so one-time query parameters cannot come back on a later
 * login. Anything other than a same-origin path (`//host`, `/\host`, a full
 * URL) gives null.
 */
export const getSavedPath = () => {
    const path = sessionStorage.getItem(PREV_PATH_KEY);
    sessionStorage.removeItem(PREV_PATH_KEY);
    if (!path || !path.startsWith('/') || path.startsWith('//') || path.startsWith('/\\')) {
        return null;
    }
    return path;
};
// StrictMode runs the guard's effect twice; start one login, not two
let loginInProgress = false;
/**
 * Start the authorisation flow.
 * Automatically detects PDE environment and uses the appropriate flow.
 */
export const getAuthorizationCode = async (config, scopes = ['openid']) => {
    if (loginInProgress) {
        return;
    }
    loginInProgress = true;
    // The query and hash carry deep links such as /agents?agent=offer.
    const { pathname, search, hash } = window.location;
    savePathBeforeAuth(`${pathname}${search}${hash}`);
    // Check for login hint
    const urlParams = new URLSearchParams(window.location.search);
    const loginHint = urlParams.get('login_hint');
    try {
        // Use PDE flow if on PDE, otherwise regular flow
        if (isPdeEnvironment()) {
            // PDE flow - only pass scopes, let startPdeAuthFlow handle state
            await startPdeAuthFlow(config, { scopes });
        }
        else {
            // Regular flow - can include login hint in state
            const authOptions = {
                scopes,
                state: loginHint ? { loginHint } : undefined,
            };
            await startAuthFlow(config, authOptions);
        }
    }
    finally {
        loginInProgress = false;
    }
};
/**
 * Exchange authorisation code for tokens.
 * Wrapper around @aecomai/auth's exchangeCodeForTokens.
 */
export const getTokenFromCode = async (config, code) => {
    const urlParams = new URLSearchParams(window.location.search);
    const state = urlParams.get('state');
    return exchangeCodeForTokens(config, code, state);
};
/**
 * Refresh tokens using refresh token.
 */
export const getTokenFromRefreshToken = async (config, refreshToken) => {
    return refreshTokens(config, refreshToken);
};
/**
 * Get session from storage.
 */
export const getSessionFromStorage = () => {
    return getSessionFromAecomai();
};
/**
 * Logout and redirect to B2C logout.
 */
export const logout = async (config) => {
    return logoutFromAecomai(config);
};
/**
 * Save session to storage and trigger state change event.
 */
export const setSessionInStorage = (auth) => {
    setSessionFromAecomai(auth);
    window.dispatchEvent(AUTH_STATE_CHANGE_EVENT);
};
// Only B2C's own verdicts; a network blip or throttling (AADB2C90229) keeps the token
const isRefreshRejected = (error) => error instanceof Error && /^(?!AADB2C90229)(AADB2C\d+|invalid_grant)\b/.test(error.message);
/** Clear a session B2C rejected; rethrow anything else. */
const dropSessionIfRejected = (error) => {
    if (!isRefreshRejected(error)) {
        throw error;
    }
    console.warn('Session refresh rejected:', error);
    clearSession();
    return null;
};
// Concurrent callers share one refresh
let pendingSession = null;
/**
 * Get session, automatically refreshing if needed.
 */
export const getSession = (config) => {
    pendingSession ??= getValidSession(config)
        .catch(dropSessionIfRejected)
        .finally(() => {
        pendingSession = null;
    });
    return pendingSession;
};
/**
 * Check if client has a valid session.
 */
export const hasValidSession = () => {
    const session = getSessionFromStorage();
    if (!session) {
        return false;
    }
    const now = Date.now();
    return now < session.idToken.expires && now < session.refreshToken.expires;
};
/**
 * Refresh OAuth session from refresh token.
 * Null means the session is over; network failures throw.
 */
export const refreshOAuthSession = async (config, auth) => {
    if (!auth || Date.now() >= auth.refreshToken.expires) {
        return null;
    }
    // B2C rotates the refresh token; keep the new one
    const newSession = await refreshTokens(config, auth.refreshToken.token).catch(dropSessionIfRejected);
    if (newSession) {
        setSessionInStorage(newSession);
    }
    return newSession;
};
